/**
 * Same-origin API base contract for cookie auth (credentials: 'include' /
 * EventSource withCredentials).
 *
 * FE cannot read backend cookie mode. When the admin surface uses credentialed
 * fetches (SSE backfill, withCredentials EventSource, dual|cookie session),
 * `apiUrl` must be empty or same-origin (relative `/api…`) so HttpOnly cookies
 * with SameSite=Lax attach.
 *
 * Forbidden copy-paste default: absolute `http://localhost:3015` while the admin
 * UI is on `:3001` — that origin pair is cross-origin and breaks Lax cookies
 * (product gallery stream `?`, credentialed preview fetch, session boot).
 *
 * Empty string → browser relative `/api/...` via Vite/Caddy proxy
 * (`VITE_DEV_API_PROXY_TARGET` is proxy upstream only, not browser apiUrl).
 *
 * @see `.env.example` — `REACT_APP_API_URL=` / `VITE_API_URL=` empty by default
 */

function isAdminBuildProd(): boolean {
  // Prefer NODE_ENV — `import.meta.env` is a Jest parse error (not catchable at runtime).
  // Vite production builds set NODE_ENV=production; Jest uses `test` → log, don't throw.
  return process.env.NODE_ENV === 'production';
}

export function assertApiBaseUrlSameOriginContract(apiBaseUrl: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const trimmed = apiBaseUrl.trim();
  if (trimmed.length === 0 || trimmed.startsWith('/')) {
    return;
  }

  let configured: URL;
  try {
    configured = new URL(trimmed, window.location.origin);
  } catch {
    const invalidMsg =
      '[rpapp-admin] Invalid VITE_API_URL / apiUrl — cookie auth may fail. Use empty apiUrl (same-origin via Caddy/Vite proxy).';
    if (isAdminBuildProd()) {
      throw new Error(invalidMsg);
    }
    const sink = globalThis['console'] as { error?: (msg: string) => void } | undefined;
    sink?.error?.(invalidMsg);
    return;
  }

  if (configured.origin === window.location.origin) {
    return;
  }

  const crossOriginMsg =
    '[rpapp-admin] apiUrl is cross-origin (e.g. http://localhost:3015 from admin :3001) — HttpOnly SameSite=Lax session cookies will not attach with credentials:include. Production contract is same-origin: leave VITE_API_URL / REACT_APP_API_URL empty and terminate /api under the admin origin (Caddy / Vite proxy). Absolute :3015 must not be the copy-paste default.';
  if (isAdminBuildProd()) {
    throw new Error(crossOriginMsg);
  }
  const sink = globalThis['console'] as { error?: (msg: string) => void } | undefined;
  sink?.error?.(crossOriginMsg);
}
